import type { RoutePoint } from '@/types/tour'
import { getTrackRoutePointEntries, normalizeTrackClientId } from '@/utils/trackRoutePoints'

export const ROUTE_POINT_ORDER_STEP = 1024

export type RoutePointOrderShift = {
  routePoint: RoutePoint
  newOrder: number
}

export type RoutePointInsertOrderPlan = {
  insertOrder: number
  shifts: RoutePointOrderShift[]
}

export type RoutePointMultiInsertOrderPlan = {
  insertOrders: number[]
  insertOrderStart: number
  shifts: RoutePointOrderShift[]
}

export type RoutePointReorderPlan = {
  reorderedRoutePoints: RoutePoint[]
  changedRoutePoints: RoutePointOrderShift[]
}

const byOrder = (left: RoutePoint, right: RoutePoint) => left.order - right.order

const collectTrackRoutePoints = (
  routePoints: RoutePoint[],
  trackClientId?: string | null,
): RoutePoint[] => {
  const normalizedTrackClientId = normalizeTrackClientId(trackClientId)
  return routePoints
    .filter(
      (routePoint) => normalizeTrackClientId(routePoint.trackClientId) === normalizedTrackClientId,
    )
    .sort(byOrder)
}

const buildSequentialShifts = (
  routePoints: RoutePoint[],
  startOrder = 0,
  step = ROUTE_POINT_ORDER_STEP,
): RoutePointOrderShift[] => {
  const shifts: RoutePointOrderShift[] = []
  routePoints.forEach((routePoint, index) => {
    const newOrder = startOrder + index * step
    if (routePoint.order !== newOrder) {
      shifts.push({ routePoint, newOrder })
    }
  })
  return shifts
}

const sameOrderIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index])

const scoreShifts = (shifts: RoutePointOrderShift[]) => ({
  changedCount: shifts.length,
  totalDelta: shifts.reduce(
    (sum, { routePoint, newOrder }) => sum + Math.abs(routePoint.order - newOrder),
    0,
  ),
})

const shouldPreferShiftSet = (
  candidate: RoutePointOrderShift[],
  currentBest: RoutePointOrderShift[] | null,
) => {
  if (!currentBest) return true

  const candidateScore = scoreShifts(candidate)
  const currentScore = scoreShifts(currentBest)
  if (candidateScore.changedCount !== currentScore.changedCount) {
    return candidateScore.changedCount < currentScore.changedCount
  }
  return candidateScore.totalDelta < currentScore.totalDelta
}

const buildRebalancedInsertPlan = (
  trackRoutePoints: RoutePoint[],
  trackInsertIndex: number,
  insertCount: number,
): RoutePointMultiInsertOrderPlan => {
  const previous = trackInsertIndex > 0 ? trackRoutePoints[trackInsertIndex - 1] : null
  const baseOrder = previous?.order ?? 0
  const insertOrders = Array.from(
    { length: insertCount },
    (_, index) => baseOrder + ROUTE_POINT_ORDER_STEP * (index + 1),
  )
  const shiftedRoutePoints = trackRoutePoints.slice(trackInsertIndex)

  return {
    insertOrders,
    insertOrderStart: insertOrders[0],
    shifts: shiftedRoutePoints
      .map((routePoint, index) => ({
        routePoint,
        newOrder: baseOrder + ROUTE_POINT_ORDER_STEP * (insertCount + index + 1),
      }))
      .filter(({ routePoint, newOrder }) => routePoint.order !== newOrder),
  }
}

const buildStartInsertPlan = (
  nextOrder: number,
  insertCount: number,
): RoutePointMultiInsertOrderPlan | null => {
  const step = Math.floor(nextOrder / (insertCount + 1))
  if (step < 1) return null

  const insertOrders = Array.from({ length: insertCount }, (_, index) => step * (index + 1))
  return {
    insertOrders,
    insertOrderStart: insertOrders[0],
    shifts: [],
  }
}

const buildMiddleInsertPlan = (
  previousOrder: number,
  nextOrder: number,
  insertCount: number,
): RoutePointMultiInsertOrderPlan | null => {
  const step = Math.floor((nextOrder - previousOrder) / (insertCount + 1))
  if (step < 1) return null

  const insertOrders = Array.from(
    { length: insertCount },
    (_, index) => previousOrder + step * (index + 1),
  )
  return {
    insertOrders,
    insertOrderStart: insertOrders[0],
    shifts: [],
  }
}

const buildGapAwareInsertPlan = (
  trackRoutePoints: RoutePoint[],
  trackInsertIndex: number,
  insertCount: number,
): RoutePointMultiInsertOrderPlan => {
  const previous = trackInsertIndex > 0 ? trackRoutePoints[trackInsertIndex - 1] : null
  const next =
    trackInsertIndex < trackRoutePoints.length ? trackRoutePoints[trackInsertIndex] : null

  if (!previous && !next) {
    const insertOrders = Array.from(
      { length: insertCount },
      (_, index) => index * ROUTE_POINT_ORDER_STEP,
    )
    return {
      insertOrders,
      insertOrderStart: insertOrders[0] ?? 0,
      shifts: [],
    }
  }

  if (!previous && next) {
    return (
      buildStartInsertPlan(next.order, insertCount) ??
      buildRebalancedInsertPlan(trackRoutePoints, trackInsertIndex, insertCount)
    )
  }

  if (previous && !next) {
    const insertOrders = Array.from(
      { length: insertCount },
      (_, index) => previous.order + ROUTE_POINT_ORDER_STEP * (index + 1),
    )
    return {
      insertOrders,
      insertOrderStart: insertOrders[0],
      shifts: [],
    }
  }

  return (
    buildMiddleInsertPlan(previous!.order, next!.order, insertCount) ??
    buildRebalancedInsertPlan(trackRoutePoints, trackInsertIndex, insertCount)
  )
}

export function getSparseTrackSeedOrders(count: number): number[] {
  if (count <= 0) return []
  return Array.from({ length: count }, (_, index) => index * ROUTE_POINT_ORDER_STEP)
}

export function getSparseTrackAppendOrder(
  routePoints: RoutePoint[],
  trackClientId?: string | null,
): number {
  const trackRoutePoints = collectTrackRoutePoints(routePoints, trackClientId)
  if (trackRoutePoints.length === 0) return 0
  return trackRoutePoints[trackRoutePoints.length - 1].order + ROUTE_POINT_ORDER_STEP
}

export function getRoutePointInsertOrderPlan(
  routePoints: RoutePoint[],
  insertIndex: number,
  trackClientId?: string | null,
): RoutePointInsertOrderPlan | null {
  const plan = getRoutePointMultiInsertOrderPlan(routePoints, insertIndex, 1, trackClientId)
  if (!plan) return null
  return {
    insertOrder: plan.insertOrders[0],
    shifts: plan.shifts,
  }
}

export function getRoutePointMultiInsertOrderPlan(
  routePoints: RoutePoint[],
  insertIndex: number,
  insertCount: number,
  trackClientId?: string | null,
): RoutePointMultiInsertOrderPlan | null {
  if (insertCount <= 0) return null
  if (insertIndex < 0 || insertIndex > routePoints.length) return null

  const entries = getTrackRoutePointEntries(routePoints, trackClientId)
  let trackInsertIndex = 0
  for (const entry of entries) {
    if (entry.globalIndex >= insertIndex) break
    trackInsertIndex += 1
  }

  return buildGapAwareInsertPlan(
    entries.map((entry) => entry.routePoint),
    trackInsertIndex,
    insertCount,
  )
}

export function getRoutePointDeleteOrderShifts(
  routePoints: RoutePoint[],
  deletedIndex: number,
): RoutePointOrderShift[] {
  if (deletedIndex < 0 || deletedIndex >= routePoints.length) return []
  return []
}

export function getRoutePointMultiDeletePlan(
  routePoints: RoutePoint[],
  deletedIndices: number[],
): {
  removedRoutePoints: RoutePoint[]
  shifts: RoutePointOrderShift[]
} {
  const validDeletedIndices = [...new Set(deletedIndices)]
    .filter((index) => index >= 0 && index < routePoints.length)
    .sort((left, right) => left - right)

  if (validDeletedIndices.length === 0) {
    return {
      removedRoutePoints: [],
      shifts: [],
    }
  }

  return {
    removedRoutePoints: validDeletedIndices.map((index) => routePoints[index]),
    shifts: [],
  }
}

export function getSparseTrackReorderPlan(
  routePoints: RoutePoint[],
  trackClientId: string,
  orderedRoutePointClientIds: string[],
): RoutePointReorderPlan {
  const trackRoutePoints = collectTrackRoutePoints(routePoints, trackClientId)
  if (trackRoutePoints.length !== orderedRoutePointClientIds.length) {
    throw new Error('Failed to reorder route points: route point count mismatch')
  }

  const routePointsById = new Map(
    trackRoutePoints.map((routePoint) => [routePoint.clientId, routePoint] as const),
  )
  const reorderedRoutePoints = orderedRoutePointClientIds.map((clientId) => {
    const routePoint = routePointsById.get(clientId)
    if (!routePoint) {
      throw new Error(`Failed to reorder route points: missing route point ${clientId}`)
    }
    return routePoint
  })

  const currentTrackIds = trackRoutePoints.map((routePoint) => routePoint.clientId)
  let bestSingleMoveChanges: RoutePointOrderShift[] | null = null

  for (let currentIndex = 0; currentIndex < currentTrackIds.length; currentIndex += 1) {
    const movedClientId = currentTrackIds[currentIndex]
    const targetIndex = orderedRoutePointClientIds.indexOf(movedClientId)
    if (targetIndex < 0 || targetIndex === currentIndex) continue

    const currentWithoutMoved = [
      ...currentTrackIds.slice(0, currentIndex),
      ...currentTrackIds.slice(currentIndex + 1),
    ]
    const targetWithoutMoved = [
      ...orderedRoutePointClientIds.slice(0, targetIndex),
      ...orderedRoutePointClientIds.slice(targetIndex + 1),
    ]
    if (!sameOrderIds(currentWithoutMoved, targetWithoutMoved)) continue

    const remainingRoutePoints = trackRoutePoints.filter(
      (routePoint) => routePoint.clientId !== movedClientId,
    )
    const insertPlan = buildGapAwareInsertPlan(remainingRoutePoints, targetIndex, 1)
    const movedRoutePoint = routePointsById.get(movedClientId)
    if (!movedRoutePoint) continue

    const candidateChanges = [
      ...(movedRoutePoint.order === insertPlan.insertOrders[0]
        ? []
        : [{ routePoint: movedRoutePoint, newOrder: insertPlan.insertOrders[0] }]),
      ...insertPlan.shifts,
    ]

    if (shouldPreferShiftSet(candidateChanges, bestSingleMoveChanges)) {
      bestSingleMoveChanges = candidateChanges
    }
  }

  const fallbackChanges = buildSequentialShifts(reorderedRoutePoints)
  const changedRoutePoints =
    bestSingleMoveChanges && shouldPreferShiftSet(bestSingleMoveChanges, fallbackChanges)
      ? bestSingleMoveChanges
      : fallbackChanges

  return {
    reorderedRoutePoints,
    changedRoutePoints,
  }
}
